import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments } from '@fortawesome/pro-light-svg-icons';
import {
  INDICATOR_SUMMARY_QUESTION_ID,
  SelectedFeedback,
} from '../../NPOFeedback/utils';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import { useFeedback } from '../FeedbackProvider';
import { LengthCounter } from '../../ReportBuilder/Components/SectionLongText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { requestInit } from 'shared/auth/hooks/useRequestState';

type ShowCorrect = 'correct' | 'question' | 'reject' | undefined;

const getPlaceholderText = (showCorrect: ShowCorrect) => {
  switch (showCorrect) {
    case 'correct':
      return 'Please explain why this is correct as is.';
    case 'question':
      return 'Feel free to add any additional comments.';
    case 'reject':
      return 'Feel free to add any additional comments.';
    default:
      return 'Feel free to add any additional comments.';
  }
};

const viewFeedbackButtonText = 'View Feedback' as const;
const getButtonText = (
  questionId: number,
  isVerificationStatus: boolean,
):
  | typeof viewFeedbackButtonText
  | 'Make Edits'
  | 'Request additional changes' => {
  if (isVerificationStatus) {
    return 'Request additional changes';
  }
  return questionId === INDICATOR_SUMMARY_QUESTION_ID
    ? viewFeedbackButtonText
    : 'Make Edits';
};

interface FeedbackSectionButtonsProps {
  questionId: number;
  selectedIndex: number;
  hideEditButton?: boolean;
  isCustomQuestion: boolean;
  question: SelectedFeedback;
  isVerificationStatus: boolean;
  handleNext: () => unknown;
  openEditModal: () => unknown;
}

const StyledDiv = styled.div`
  .feedback-section-buttons {
    display: flex;
    justify-content: space-between;
    gap: ${SPACERS.small};
    margin-top: ${SPACERS.medium};
    & > button {
      flex: 1;
    }
  }
  .feedback-resolved-error {
    text-align: center;
    margin-top: ${SPACERS.medium};
    margin-bottom: ${SPACERS.medium};
  }
  .feedback-show-correct-heading {
    text-align: center;
    margin-top: ${SPACERS.small};
    margin-bottom: ${SPACERS.tiny};
  }
  .feedback-submit-button {
    width: 300px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const FeedbackSectionButtons: React.FC<FeedbackSectionButtonsProps> = ({
  questionId,
  isCustomQuestion,
  hideEditButton,
  isVerificationStatus,
  selectedIndex,
  question,
  handleNext,
  openEditModal,
}) => {
  const [text, setText] = useState<string>('');
  const [showCorrect, setShowCorrect] = useState<ShowCorrect>(undefined);

  const {
    commentRequestState,
    markAsResolvedRequestState,
    markAsVerified,
    handleDoneQuestion,
    setMarkAsResolvedRequest,
  } = useFeedback();

  useEffect(() => {
    if (markAsResolvedRequestState.type === 'REQUEST_SUCCESS') {
      setMarkAsResolvedRequest(requestInit());
      handleNext();
    }
  }, [markAsResolvedRequestState]);

  useEffect(() => {
    setText('');
    setShowCorrect(undefined);
  }, [selectedIndex]);

  useEffect(() => {
    if (markAsResolvedRequestState.type === 'REQUEST_SUCCESS') {
      setText('');
      setShowCorrect(undefined);
    }
  }, [markAsResolvedRequestState]);

  const handleReject = useCallback(() => {
    if (isVerificationStatus) {
      setShowCorrect(s => (s === 'reject' ? undefined : 'reject'));
      return;
    }
    openEditModal();
    return;
  }, [isVerificationStatus, setShowCorrect, openEditModal]);

  const handleCorrect = useCallback(() => {
    if (isVerificationStatus) {
      markAsVerified(questionId, isCustomQuestion, {
        verified: true,
      });
      return;
    }
    setShowCorrect(s => (s === 'correct' ? undefined : 'correct'));
    return;
  }, [
    questionId,
    isVerificationStatus,
    isCustomQuestion,
    markAsVerified,
    setShowCorrect,
  ]);

  const handleSubmit = () => {
    if (isVerificationStatus) {
      markAsVerified(questionId, isCustomQuestion, {
        verified: false,
        text,
      });
      return;
    }
    handleDoneQuestion(
      questionId,
      question,
      showCorrect === 'correct',
      text,
      undefined,
      undefined,
      isCustomQuestion,
    );
  };

  const showOnTop = showCorrect === undefined;
  const isError = markAsResolvedRequestState.type === 'REQUEST_ERROR';

  const buttonText = getButtonText(questionId, isVerificationStatus);
  const isViewFeedback = buttonText === viewFeedbackButtonText;

  return (
    <StyledDiv>
      {showOnTop && isError && (
        <TrueImpactText color="critical" className="feedback-resolved-error">
          {markAsResolvedRequestState.error.message}
        </TrueImpactText>
      )}
      <div className="feedback-section-buttons">
        {!hideEditButton && (
          <TrueImpactButton
            variant={isViewFeedback ? 'primary-large' : 'secondary-large'}
            color={showCorrect === 'reject' ? 'critical' : undefined}
            backgroundColor={
              showCorrect === 'reject' ? 'criticalBackground' : undefined
            }
            onClick={handleReject}
          >
            {isViewFeedback && <FontAwesomeIcon icon={faComments} />}
            {buttonText}
          </TrueImpactButton>
        )}
        <TrueImpactButton
          variant={
            showCorrect === 'correct' ? 'primary-large' : 'secondary-large'
          }
          isLoading={markAsResolvedRequestState.type === 'REQUEST_START'}
          onClick={handleCorrect}
        >
          {isVerificationStatus ? 'Accept the revisions' : 'It’s Correct As Is'}
        </TrueImpactButton>
      </div>
      {showCorrect && (
        <>
          <TrueImpactText
            variant="body-bold"
            className="feedback-show-correct-heading"
          >
            {getPlaceholderText(showCorrect)}
          </TrueImpactText>
          <TrueImpactTextfield
            id="feedback-textfield"
            autoFocus={true}
            isTextarea={true}
            height={193}
            placeholder={getPlaceholderText(showCorrect)}
            value={text}
            onChange={setText}
          />
          <LengthCounter minLength={5} maxLength={900} length={text.length} />
          {!showOnTop && isError && (
            <TrueImpactText
              color="critical"
              className="feedback-resolved-error"
            >
              {markAsResolvedRequestState.error.message}
            </TrueImpactText>
          )}
          {commentRequestState.type === 'REQUEST_ERROR' && (
            <TrueImpactText
              color="critical"
              className="feedback-resolved-error"
            >
              {commentRequestState.error.message}
            </TrueImpactText>
          )}
          <TrueImpactButton
            variant="primary-large"
            className="feedback-submit-button"
            onClick={handleSubmit}
            disabled={text.length < 5 || text.length > 900}
          >
            Submit
          </TrueImpactButton>
        </>
      )}
    </StyledDiv>
  );
};

export default FeedbackSectionButtons;
